import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import * as Flags from "country-flag-icons/react/3x2";
import { getData } from "country-list";
import { Check } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

interface CountrySelectProps {
  value?: string;
  onSelect?: (country?: string) => void;
  placeholder?: string;
  empyPlaceholder?: string;
  searchPlaceholder?: string;
}

export const CountrySelect = ({
  value: propValue,
  searchPlaceholder,
  onSelect,
  placeholder,
  empyPlaceholder,
}: CountrySelectProps) => {
  const [search, setSearch] = useState("");
  const [value, setValue] = useState(propValue);

  useEffect(() => {
    setValue(propValue ?? value);
  }, [propValue]);

  const countriesOptions = useMemo(() => {
    const data = getData();

    return data.map((country) => ({ value: country.code, label: country.name }));
  }, []);

  const selectedCountry = useMemo(() => {
    return countriesOptions.find((option) => option.label === value);
  }, [countriesOptions, value]);

  const Flag = selectedCountry ? Flags[selectedCountry.value as keyof typeof Flags] : undefined;

  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            id="date-picker-range"
            variant="outline"
            className="flex-1 justify-start px-2.5 font-normal"
          >
            {Flag && <Flag />}

            {selectedCountry ? selectedCountry.label : (placeholder ?? "Please select country")}
          </Button>
        }
      />

      <PopoverContent className="w-auto p-0" align="start">
        <Command>
          <CommandInput
            placeholder={searchPlaceholder ?? "Search country..."}
            value={search}
            onValueChange={setSearch}
          />
          <CommandEmpty>{empyPlaceholder ?? "No country found."}</CommandEmpty>

          <CommandGroup className="max-h-64 overflow-auto">
            {countriesOptions.map((country) => {
              const OptionFlag = Flags[country.value as keyof typeof Flags];
              return (
                <CommandItem
                  key={country.value}
                  value={country.label}
                  onSelect={() => {
                    if (country.value === selectedCountry?.value) {
                      onSelect?.(undefined);
                      setValue(undefined);
                    } else {
                      onSelect?.(country.label);
                      setValue(country.label);
                    }
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 size-4",
                      selectedCountry?.value === country.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <OptionFlag />
                  <span>
                    <span className="text-muted-foreground">({country.value})</span> {country.label}
                  </span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
